import { useEffect, useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import type { RootState } from '../../../../store/store';
import type {
  PropertyCartItem,
  AssetCartItem,
  EntityGroup,
  EntityInfo,
} from '../../cart/types/cart.types';
import {
  transformToPropertyCartItems,
  transformToAssetCartItems,
  type OrderItem,
  type BulkOrderResponseData,
} from '../utils/orderTransforms';

// ---------------------------------------------------------------------------
// Location state shape
// ---------------------------------------------------------------------------

interface LocationState {
  isCartOrder?: boolean;
  isBulkOrder?: boolean;
  bulkOrderResponseData?: BulkOrderResponseData;
  serviceImageMap?: Record<string, string>;
  entityMap?: Record<string, EntityInfo>;
}

// ---------------------------------------------------------------------------
// Grouped assets type
// ---------------------------------------------------------------------------

export interface GroupedAssets {
  propertyUUID: string;
  propertyAddress: string;
  entityName?: string;
  items: AssetCartItem[];
  subtotal: number;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useOrderConfirmationData() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { confirmationData } = useSelector((state: RootState) => state.order);

  const [propertyCartItems, setPropertyCartItems] = useState<
    PropertyCartItem[]
  >([]);
  const [assetCartItems, setAssetCartItems] = useState<AssetCartItem[]>([]);
  const [isDataReady, setIsDataReady] = useState(false);
  const [responseData, setResponseData] =
    useState<BulkOrderResponseData | null>(null);
  const [propertyOrders, setPropertyOrders] = useState<OrderItem[]>([]);
  const [assetOrders, setAssetOrders] = useState<OrderItem[]>([]);

  const locationState = location.state as LocationState | null;

  // -----------------------------------------------------------------------
  // Derived state
  // -----------------------------------------------------------------------

  const hasPropertyItems = propertyCartItems.length > 0;
  const hasAssetItems = assetCartItems.length > 0;
  const hasBothTypes = hasPropertyItems && hasAssetItems;

  // Group assets by property
  const groupedAssets = useMemo<GroupedAssets[]>(() => {
    const grouped = assetCartItems.reduce<Record<string, GroupedAssets>>(
      (acc, item) => {
        const key = item.propertyUUID;
        if (!acc[key]) {
          acc[key] = {
            propertyUUID: key,
            propertyAddress: item.propertyAddress,
            entityName: item.entityName,
            items: [],
            subtotal: 0,
          };
        }
        acc[key].items.push(item);
        acc[key].subtotal += item.price;
        return acc;
      },
      {},
    );
    return Object.values(grouped);
  }, [assetCartItems]);

  // Group property items by entity
  const propertyItemsByEntity = useMemo<
    EntityGroup<PropertyCartItem>[]
  >(() => {
    const grouped = propertyCartItems.reduce<
      Record<string, EntityGroup<PropertyCartItem>>
    >((acc, item) => {
      const key = item.entityUUID || 'default';
      if (!acc[key]) {
        acc[key] = {
          entityUUID: item.entityUUID || '',
          entityName: item.entityName || '',
          items: [],
          subtotal: 0,
        };
      }
      acc[key].items.push(item);
      acc[key].subtotal += item.price || 0;
      return acc;
    }, {});
    return Object.values(grouped);
  }, [propertyCartItems]);

  const hasMultipleEntities = useMemo(() => {
    const allEntityUUIDs = new Set([
      ...propertyCartItems.map((i) => i.entityUUID).filter(Boolean),
      ...assetCartItems.map((i) => i.entityUUID).filter(Boolean),
    ]);
    return allEntityUUIDs.size > 1;
  }, [propertyCartItems, assetCartItems]);

  // -----------------------------------------------------------------------
  // Load from Redux confirmation data (single order flow)
  // -----------------------------------------------------------------------

  useEffect(() => {
    if (!confirmationData) return;
    const items = confirmationData.items || [];
    setResponseData(confirmationData);
    setPropertyOrders(items.filter((item: OrderItem) => !item.assetUUID));
    setAssetOrders(items.filter((item: OrderItem) => item.assetUUID));
    setIsDataReady(true);
  }, [confirmationData]);

  // -----------------------------------------------------------------------
  // Load from location state or URL params (cart / bulk / URL)
  // -----------------------------------------------------------------------

  useEffect(() => {
    const dataParam = searchParams.get('data');
    let parsedData: BulkOrderResponseData | null = null;
    if (dataParam) {
      try {
        parsedData = JSON.parse(dataParam) as BulkOrderResponseData;
      } catch {
        parsedData = null;
      }
    }

    if (locationState?.isCartOrder && locationState.bulkOrderResponseData) {
      const items = locationState.bulkOrderResponseData.items;
      setResponseData(locationState.bulkOrderResponseData);
      setPropertyOrders(items.filter((o) => !o.assetUUID));
      setAssetOrders(items.filter((o) => o.assetUUID));
      return;
    }

    if (locationState?.isBulkOrder && locationState.bulkOrderResponseData) {
      setResponseData(locationState.bulkOrderResponseData);
      setPropertyOrders(locationState.bulkOrderResponseData.items);
      return;
    }

    if (parsedData) {
      setResponseData(parsedData);
      setPropertyOrders(parsedData.items.filter((o) => !o.assetUUID));
      setAssetOrders(parsedData.items.filter((o) => o.assetUUID));
    }
  }, [searchParams, locationState]);

  // -----------------------------------------------------------------------
  // Transform property orders
  // -----------------------------------------------------------------------

  useEffect(() => {
    if (propertyOrders.length === 0) return;
    const serviceImageMap =
      locationState?.serviceImageMap ||
      (responseData as BulkOrderResponseData | null)?.serviceImageMap;
    const entityMap =
      locationState?.entityMap ||
      (responseData as BulkOrderResponseData | null)?.entityMap;
    const items = transformToPropertyCartItems(
      propertyOrders,
      serviceImageMap,
      entityMap,
    );
    setPropertyCartItems(items);
    setIsDataReady(true);
  }, [propertyOrders, locationState, responseData]);

  // -----------------------------------------------------------------------
  // Transform asset orders
  // -----------------------------------------------------------------------

  useEffect(() => {
    if (assetOrders.length === 0) return;
    const entityMap =
      locationState?.entityMap ||
      (responseData as BulkOrderResponseData | null)?.entityMap;
    const items = transformToAssetCartItems(assetOrders, entityMap);
    setAssetCartItems(items);
    setIsDataReady(true);
  }, [assetOrders, locationState, responseData]);

  return {
    propertyCartItems,
    assetCartItems,
    isDataReady,
    hasPropertyItems,
    hasAssetItems,
    hasBothTypes,
    groupedAssets,
    propertyItemsByEntity,
    hasMultipleEntities,
  };
}
